import { contentStyles } from "../../content";
import {
  ContentText,
  ContentWrapper,
  HeadlineSmall,
  HeadlineTiny,
  Image,
  RichTextContent,
} from "../../content/components";
import { useVisibleGuestList } from "../../contexts/VisibleGuestListContext";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import { isEmptySlateDocument } from "../../core/constants";
import { countryNameByCode } from "../../guest-management/forms/constants";
import { Configuration, Photo, WebsiteEvent } from "../../resources/modelTypes";
import { getPhotoByPk, RootState } from "../../resources/reduxTypes";
import { getEventDateTimeComponent } from "../utils";
import { EventDetail } from "./EventDetail";
import ListNumber from "./ListNumber";
import { css } from "@emotion/react";
import { ActionLink, MediumHeadline } from "@minted/fancyclothes";
import { styleUtils, theme } from "@minted/minted-components";
import React from "react";
import { connect } from "react-redux";

const wordWrapStyles = css`
  overflow: hidden;
`;

const eventHeadingStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const eventNumMarginStyles = css`
  margin-right: ${theme.spacing.x3};
`;

const eventStyles = {
  attending: css`
    margin-top: ${theme.spacing.x4};
    margin-bottom: ${theme.spacing.x4};
  `,
  attendingWrapper: css`
    margin-top: ${theme.spacing.x12};
  `,
  eventInfo: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: ${styleUtils.rem(780)};
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${styleUtils.rem(40)};
    ${theme.media.greaterThan(theme.breakpoints.medium)(`
      flex-direction: row;
    `)};
  `,
  guestName: css`
    list-style-type: none;
  `,
  infoBlock: css`
    flex: 1 1 calc(100% / 3);
    ${theme.media.lessThan(theme.breakpoints.medium)(`
      margin-bottom: ${theme.spacing.x4};
    `)};
    word-break: break-all;
  `,
  infoValue: css`
    margin-top: ${theme.spacing.x3};
    margin-bottom: ${theme.spacing.x4};
  `,
  schedule: css`
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(620)};
  `,
  wrapper: css``,
};

export type EventListNumberProps = {
  number: number;
  showListNumber: boolean;
};

const EventListNumber = ({ number, showListNumber }: EventListNumberProps) =>
  showListNumber ? (
    <div css={eventNumMarginStyles}>
      {/* @ts-expect-error DesignComponent does not work well with typescript*/}
      <ListNumber>{number}</ListNumber>
    </div>
  ) : null;

export type EventProps = {
  configuration: Configuration;
  event: WebsiteEvent;
  index: number;
  showListNumber: boolean;
};

type EventPropsWithRedux = {
  fullPhoto: Photo | null;
} & EventProps;

const Event = ({
  configuration,
  event,
  fullPhoto,
  index,
  showListNumber,
}: EventPropsWithRedux) => {
  const {
    address1,
    address2,
    city,
    country,
    date,
    description,
    endDate,
    endTime,
    formattedAddress,
    hasPhysicalAddress,
    hostedBy,
    image,
    showAttending,
    showMaybe,
    showNotAttending,
    showNotReplied,
    state,
    time,
    timezone,
    title,
    venue,
    zipCode,
  } = event;
  const site = useCurrentSite();
  const { isOnlineInvitation, isWedding } = site;
  const { guests, hasLoaded: hasLoadedGuestList } = useVisibleGuestList();

  const attendingGuests = guests.filter((guest) => guest.rsvpStatus === "yes");
  const notAttendingGuests = guests.filter(
    (guest) => guest.rsvpStatus === "no",
  );
  const maybeGuests = guests.filter((guest) => guest.rsvpStatus === "maybe");
  const notRepliedGuests = guests.filter((guest) => guest.rsvpStatus == null);

  const attendingCount = attendingGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const notAttendingCount = notAttendingGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const maybeCount = maybeGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);
  const notRepliedCount = notRepliedGuests
    .map((guest) => guest.count)
    .reduce((total, guestCount) => total + guestCount, 0);

  if (isWedding) {
    return (
      <ContentWrapper
        extraCss={[contentStyles.relative, contentStyles.mx0Md]}
        imageAlignment={configuration.imageAlignment}
        textAlignment={configuration.textAlignment}
      >
        <Image
          image={image || fullPhoto?.original}
          imageAlignment={configuration.imageAlignment}
          imageSize={configuration.imageSize}
        />

        <ContentText
          extraCss={[contentStyles.flex, contentStyles.flexRow]}
          textAlignment={configuration.textAlignment}
        >
          <div
            css={[
              image ? contentStyles.flex5 : contentStyles.flex9,
              wordWrapStyles,
            ]}
          >
            <div css={eventHeadingStyles} data-cy="subheading">
              <EventListNumber
                number={index + 1}
                showListNumber={showListNumber}
              />
              <HeadlineSmall>{title}</HeadlineSmall>
            </div>
            <div data-cy="body">
              <EventDetail
                address={formattedAddress}
                country={hasPhysicalAddress ? country : null}
                date={date}
                endDate={endDate}
                endTime={endTime}
                time={time}
                timezone={timezone}
                venue={venue}
              />

              {description && <RichTextContent value={description} />}

              {hasPhysicalAddress && (
                <ActionLink
                  target="_blank"
                  text="Get Directions"
                  url={"https://www.google.com/maps/dir/?api=1&destination=".concat(
                    encodeURIComponent(formattedAddress),
                  )}
                />
              )}
            </div>
          </div>
        </ContentText>
      </ContentWrapper>
    );
  } else if (isOnlineInvitation) {
    return (
      <ContentWrapper
        extraCss={[
          contentStyles.relative,
          contentStyles.mx0Md,
          eventStyles.wrapper,
        ]}
        imageAlignment={configuration.imageAlignment}
        noVerticalMargin
        textAlignment={configuration.textAlignment}
      >
        <div css={eventStyles.eventInfo}>
          {hostedBy?.trim() && (
            <div css={eventStyles.infoBlock}>
              <HeadlineTiny>Hosted By</HeadlineTiny>
              <div css={eventStyles.infoValue}>{hostedBy}</div>
            </div>
          )}
          <div css={eventStyles.infoBlock}>
            <HeadlineTiny>Date</HeadlineTiny>
            <div css={eventStyles.infoValue}>
              {getEventDateTimeComponent(
                date,
                time,
                endDate,
                endTime,
                timezone,
              )}
            </div>
          </div>
          {(venue || hasPhysicalAddress) && (
            <div css={eventStyles.infoBlock}>
              <HeadlineTiny>Address</HeadlineTiny>
              <div css={eventStyles.infoValue}>
                {venue && venue !== address1 && (
                  <>
                    {venue}
                    <br />
                  </>
                )}
                {hasPhysicalAddress && (
                  <>
                    {address1 && (
                      <>
                        {address1}
                        {address2 ? `, ${address2}` : ""}
                        <br />
                      </>
                    )}
                    {city || state || zipCode
                      ? `${city || ""}, ${state || ""} ${zipCode || ""}`
                      : ""}
                    {country && country !== "US" && (
                      <>
                        <br />
                        {countryNameByCode[country]}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        {!isEmptySlateDocument(description) && (
          <div>
            <MediumHeadline>Event Schedule</MediumHeadline>
            <div css={eventStyles.schedule}>
              <RichTextContent value={description} />
            </div>
          </div>
        )}
        {hasLoadedGuestList &&
          (showAttending ||
            showNotAttending ||
            showNotReplied ||
            showMaybe) && (
            <div css={eventStyles.attendingWrapper}>
              <MediumHeadline>Guest List</MediumHeadline>
              {showAttending && (
                <>
                  <div css={eventStyles.attending}>
                    {`${attendingCount} ATTENDING`}
                  </div>
                  {attendingGuests.map((guest, index) => (
                    <li css={eventStyles.guestName} key={`attending-${index}}`}>
                      {guest.name} {guest.count > 1 && `(${guest.count})`}
                    </li>
                  ))}
                </>
              )}
              {showMaybe && (
                <>
                  <div css={eventStyles.attending}>{`${maybeCount} MAYBE`}</div>
                  {maybeGuests.map((guest, index) => (
                    <li css={eventStyles.guestName} key={`maybe-${index}}`}>
                      {guest.name} {guest.count > 1 && `(${guest.count})`}
                    </li>
                  ))}
                </>
              )}
              {showNotAttending && (
                <>
                  <div css={eventStyles.attending}>
                    {`${notAttendingCount} NOT ATTENDING`}
                  </div>
                  {notAttendingGuests.map((guest, index) => (
                    <li
                      css={eventStyles.guestName}
                      key={`notAttending-${index}}`}
                    >
                      {guest.name} {guest.count > 1 && `(${guest.count})`}
                    </li>
                  ))}
                </>
              )}

              {showNotReplied && notRepliedGuests.length > 0 && (
                <>
                  <div css={eventStyles.attending}>
                    {`${notRepliedCount} NOT YET REPLIED`}
                  </div>
                  {notRepliedGuests.map((guest, index) => (
                    <li
                      css={eventStyles.guestName}
                      key={`not-replied-${index}}`}
                    >
                      {guest.name} {guest.count > 1 && `(${guest.count})`}
                    </li>
                  ))}
                </>
              )}
            </div>
          )}
      </ContentWrapper>
    );
  }
};

const mapStateToProps = (state: RootState, props: EventProps) => ({
  fullPhoto: getPhotoByPk(state, props.event.photo),
});

export default connect(mapStateToProps)(Event) as React.FC<EventProps>;
